<template>
  <section class="aboutus-section">
    <h2 class="aboutus-title">{{ t('aboutUs.intro.title') }}</h2>
    <div class="aboutus-title-underline"></div>
    <div class="aboutus-intro-content">
      <div class="aboutus-intro-text">
        <div class="intro_title">{{ t('aboutUs.intro.companyName') }}</div>
        <span v-html="t('aboutUs.intro.description')"></span>
      </div>
      <div class="aboutus-intro-img">
        <img :src="t('aboutUs.intro.introduceImage')" alt="公司环境" />
      </div>
    </div>
    <div class="aboutus-data-row" ref="statsRef">
      <div v-for="(item, index) in animatedStats" :key="index" class="aboutus-data-item">
        <div class="aboutus-data-num">
          <span>{{ item.displayNumber }}</span>

          <template v-if="index === 3">
            <span class="suffix-small">{{ (item.suffix.split('+')[0] || '') }}</span>
            <span v-if="item.suffix.includes('+')" class="sup">+</span>
            <span class="suffix-small">{{ (item.suffix.split('+')[1] || '') }}</span>
          </template>

          <template v-else>
            <span v-if="[1, 2].includes(index) && item.suffix.includes('+')">
              <span class="sup">+</span>
            </span>
            <span :class="{ 'suffix-small': [0, 1, 2, 3, 4, 5].includes(index) }">
              {{ item.suffix.replace('+', '') }}
            </span>
          </template>
        </div>
        <div class="aboutus-data-label">{{ item.label }}</div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'

const { t, tm } = useI18n()

const stats = computed(() => {
  const statsData = tm('aboutUs.intro.stats')
  return Array.isArray(statsData) ? statsData : []
})

const animatedStats = ref(stats.value.map(item => ({ ...item, displayNumber: 0, suffix: '' })))
const statsRef = ref(null)
let observer = null

const startCounting = () => {
  animatedStats.value = stats.value.map(item => ({ ...item, displayNumber: 0, suffix: '' }))

  animatedStats.value.forEach(item => {
    // 去掉逗号再提取数字（适配英文 2,000,000+）
    const numPart = (item.number || '').replace(/,/g, '');
    const match = numPart.match(/\d+/);
    const targetNumber = match ? parseInt(match[0], 10) : 0;

    // 从原始 number 中去掉“起始的数字+逗号”，保留后缀（如 '万+台' / '+ units'）
    const suffix = (item.number || '').replace(/^\s*\d[\d,]*/, '');
    item.suffix = suffix;

    let current = 0;
    const duration = 2000;
    const interval = 50;
    const steps = Math.ceil(duration / interval);
    const increment = targetNumber / steps;

    const timer = setInterval(() => {
      current += increment;
      if (current >= targetNumber) {
        current = targetNumber;
        clearInterval(timer);
      }
      item.displayNumber = Math.floor(current);
    }, interval);
  });
};


watch(stats, () => {
  startCounting()
})

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        startCounting()
        observer.unobserve(entry.target)
      }
    })
  })

  if (statsRef.value) {
    observer.observe(statsRef.value)
  }
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style scoped>
.aboutus-section {
  width: 100%;
  margin: 0 auto;
  background: #fff;
}

.aboutus-title {
  margin-top: 80px;
  text-align: center;
  /* 需要适配 */
  font-size: 2.4rem;
  font-weight: bold;
  color: #222;
  margin-bottom: 8px;
  position: relative;
}

.aboutus-title-underline {
  width: 80px;
  height: 4px;
  background: #ff7f1a;
  margin: 10px auto 80px auto;
}

.aboutus-intro-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 40px;
  margin-bottom: 100px;
}

.aboutus-intro-text {
  flex: 1;
  font-size: 1.08rem;
  color: #333;
}

.intro_title {
  /* 需要适配 */
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 10px;
}

.intro_title::after {
  content: '';
  display: block;
  width: 7cqb;
  height: 4px;
  background: rgba(0, 0, 0, 0.817);
  margin: 0;
}


.aboutus-intro-img {
  flex: 1;
  /* min-width: 220px;
  max-width: 340px; */
}

.aboutus-intro-img img {
  width: 100%;
  border-radius: 2px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.aboutus-data-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10vh 0;
  justify-items: center;
  margin: 0 auto;
  background-color: #f7f7f7;
  padding-top: 4%;
  padding-bottom: 4%;
  padding-left: 12%;
  padding-right: 12%;
}

.aboutus-data-item {
  text-align: center;
  width: 100%;
}

.aboutus-data-num {
  color: #ff7f1a;
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 8px;

}

.aboutus-data-label {
  color: #333;
  font-size: 1rem;
}

.suffix-small {
  font-size: 0.6em;
}

.sup {
  vertical-align: super;
  font-size: 0.6em;
}


/* 大屏幕设备 (1400px 以上) */
@media screen and (min-width: 1400px) {
  .aboutus-title {
    font-size: 2.4rem;
    margin-top: 80px;
  }

  .intro_title {
    font-size: 2rem;
  }

  .aboutus-intro-text {
    font-size: 1.2rem;
    line-height: 2.6;
  }

  .aboutus-intro-content {
    gap: 20px;
    margin-bottom: 100px;
    margin-left: 5%;
    margin-right: 5%;
  }

  .aboutus-data-num {
    font-size: 3rem;
  }

  .aboutus-data-label {
    font-size: 1.3rem;
  }

  .aboutus-title-underline {
    width: 80px;
    margin: 10px auto 80px auto;
  }

  .intro_title::after {
    width: 70px;
  }
}

@media screen and (max-width: 1800px) {
  .aboutus-intro-text {
    font-size: 1.2rem;
    line-height: 2;
  }
}

/* 笔记本电脑 */
@media screen and (max-width: 1399px) {
  .aboutus-intro-content {
    margin-left: 7%;
    margin-right: 7%;
    margin-bottom: 90px;
    gap: 20px;
  }

  .aboutus-title {
    font-size: 2.2rem;
    margin-top: 70px;
  }

  .intro_title {
    font-size: 1.8rem;
  }

  .aboutus-intro-text {
    font-size: 1rem;
    line-height: 1.5;
  }

  .aboutus-data-num {
    font-size: 2.7rem;
  }

  .aboutus-data-label {
    font-size: 1.2rem;
  }

  .aboutus-title-underline {
    width: 70px;
    margin: 10px auto 70px auto;
  }

  .intro_title::after {
    width: 60px;
  }
}

/* 平板设备 */
@media screen and (max-width: 991px) {
  .aboutus-title {
    font-size: 2rem;
    margin-top: 60px;
  }

  .intro_title {
    font-size: 1.4rem;
  }

  .aboutus-intro-text {
    font-size: 0.9rem;
    line-height: 1.3;
  }

  .aboutus-intro-content {
    margin-left: 5%;
    margin-right: 5%;
    gap: 20px;
    margin-bottom: 80px;
  }

  .aboutus-data-num {
    font-size: 2.3rem;
  }

  .aboutus-data-label {
    font-size: 1.1rem;
  }

  .aboutus-data-item {
    height: 140px;
    padding-bottom: 30px;
  }

  .aboutus-data-num {
    margin-top: 50px;
    margin-bottom: 20px;
  }

  .aboutus-data-row {
    gap: 0 0;
  }

  .aboutus-title-underline {
    width: 60px;
    margin: 10px auto 60px auto;
  }

  .intro_title::after {
    width: 50px;
  }
}

/* 手机设备 */
@media screen and (max-width: 767px) {
  .aboutus-title {
    font-size: 1.8rem;
    margin-top: 50px;
  }

  .intro_title {
    font-size: 1.1rem;
  }

  .aboutus-intro-text {
    font-size: 0.7rem;
    line-height: 1.2;
  }

  .aboutus-intro-content {
    margin-left: 3%;
    margin-right: 3%;
    gap: 20px;
    margin-bottom: 60px;
  }

  .aboutus-data-num {
    font-size: 1.8rem;
  }

  .aboutus-data-label {
    font-size: 0.9rem;
  }

  .aboutus-title-underline {
    width: 80px;
    height: 3px;
  }

  .intro_title::after {
    width: 60px;
    height: 3px;
  }

  .aboutus-data-item {
    height: 120px;
    padding-bottom: 20px;
  }

  .aboutus-data-num {
    margin-top: 40px;
    margin-bottom: 15px;

  }

  .aboutus-title-underline {
    width: 50px;
    margin: 10px auto 50px auto;
  }

  .intro_title::after {
    width: 40px;
  }
}
</style>
